<template>
    <vx-box class="p-4">
        <div class="flex justify-between items-start mb-4">
            <div>
                <meta-badge
                    v-if="senderBadge"
                    :icon="senderBadge.icon"
                    :color="senderBadge.color"
                    :data-test="`${campaign.sender.type}-badge`"
                >
                    {{ senderBadge.label }}
                </meta-badge>

                <div class="text-base text-slate-800">{{ campaign.name }}</div>
            </div>
            <div v-if="showDropdown" class="hidden sm:block">
                <vx-dropdown placement="right">
                    <vx-button size="sm" type="button" color="muted" outline class="w-[37px]">
                        <font-awesome-icon widthAuto :icon="faEllipsis" />
                    </vx-button>

                    <template v-slot:items>
                        <vx-dropdown-item
                            v-if="campaign.status === 'scheduled'"
                            data-test="set-to-draft"
                            @click="setToDraft()"
                        >
                            Set to Draft
                            <font-awesome-icon widthAuto :icon="faPause"></font-awesome-icon>
                        </vx-dropdown-item>
                        <vx-dropdown-item
                            v-if="canEditMessage"
                            data-test="edit-message"
                            @click="editMessageModalVisible = true"
                        >
                            Edit Message
                            <font-awesome-icon widthAuto :icon="faFilePen"></font-awesome-icon>
                        </vx-dropdown-item>
                        <vx-dropdown-item
                            v-if="canReschedule"
                            data-test="reschedule"
                            @click="rescheduleModalVisible = true"
                        >
                            Reschedule
                            <font-awesome-icon widthAuto :icon="faCalendar"></font-awesome-icon>
                        </vx-dropdown-item>
                        <vx-dropdown-item v-if="showGoToCampaign" data-test="go-to-campaign" :href="goToCampaignHref">
                            Go To Campaign
                            <font-awesome-icon widthAuto :icon="faSquareArrowUpRight" />
                        </vx-dropdown-item>
                        <vx-dropdown-item v-if="showGoToContext" data-test="go-to-context" @click="goToContext()">
                            Go To Context
                            <font-awesome-icon widthAuto :icon="faSquareArrowUpRight" />
                        </vx-dropdown-item>
                        <vx-dropdown-item data-test="delete" @click="deleteCampaign()">
                            Delete
                            <font-awesome-icon widthAuto :icon="faTrash"></font-awesome-icon>
                        </vx-dropdown-item>
                    </template>
                </vx-dropdown>
            </div>
        </div>

        <div class="flex flex-col gap-4 sm:flex-row sm:justify-between">
            <div class="flex flex-col gap-1">
                <div class="flex items-center gap-1 text-xs">
                    <div class="text-slate-500">Status:</div>
                    <div>
                        <campaign-badge icon :status="campaign.status" />
                    </div>
                </div>
                <div class="flex items-center gap-1 text-xs">
                    <div class="text-slate-500">Scheduled:</div>
                    <div>
                        {{ toShortDateTimeRelativeDays(campaign.live_at, props.timezone) }}
                    </div>
                </div>
                <div class="flex items-center gap-1 text-xs">
                    <div class="text-slate-500">Estimated Messages:</div>
                    <div>
                        <vx-popover
                            class="cursor-pointer"
                            arrow
                            placement="top"
                            @click="copyText(campaign.audience_size)"
                            hover
                        >
                            {{ vxFormatCount(campaign.audience_size) }}
                            <template v-slot:content>
                                <div class="text-center" @click="copyText(campaign.audience_size)">
                                    {{ Intl.NumberFormat('en-US').format(campaign.audience_size) }}
                                    <div class="text-xs pt-1 text-slate-500">Click to copy</div>
                                </div>
                            </template>
                        </vx-popover>
                    </div>
                </div>
                <div class="flex items-center gap-1 text-xs">
                    <div class="text-slate-500">Estimated SMS Segments:</div>
                    <div>
                        {{ campaign.sms_segments }}
                    </div>
                </div>
                <div v-if="showEstimatedCost" class="flex items-center gap-1 text-xs">
                    <div class="text-slate-500">Estimated Cost:</div>
                    <div>
                        {{ campaign.estimated_cost != null ? currencyCents(campaign.estimated_cost) : '-' }}
                    </div>
                </div>
            </div>

            <div class="sm:max-w-1/2">
                <message-cloud fullWidth :message="campaign.main_message" :showDetails="false" />
            </div>
        </div>

        <div v-if="showDropdown" class="w-full mt-4 block sm:hidden">
            <vx-dropdown placement="right">
                <vx-button size="sm" type="button" color="muted-light" class="w-full sm:w-[37px]">
                    <font-awesome-icon widthAuto :icon="faEllipsis" />
                </vx-button>

                <template v-slot:items>
                    <vx-dropdown-item
                        v-if="campaign.status === 'scheduled'"
                        data-test="set-to-draft"
                        @click="setToDraft()"
                    >
                        Set to Draft
                        <font-awesome-icon widthAuto :icon="faPause"></font-awesome-icon>
                    </vx-dropdown-item>
                    <vx-dropdown-item
                        v-if="canEditMessage"
                        data-test="edit-message"
                        @click="editMessageModalVisible = true"
                    >
                        Edit Message
                        <font-awesome-icon widthAuto :icon="faFilePen"></font-awesome-icon>
                    </vx-dropdown-item>
                    <vx-dropdown-item
                        v-if="canReschedule"
                        data-test="reschedule"
                        @click="rescheduleModalVisible = true"
                    >
                        Reschedule
                        <font-awesome-icon widthAuto :icon="faCalendar"></font-awesome-icon>
                    </vx-dropdown-item>
                    <vx-dropdown-item v-if="showGoToCampaign" data-test="go-to-campaign" :href="goToCampaignHref">
                        Go To Campaign
                        <font-awesome-icon widthAuto :icon="faSquareArrowUpRight" />
                    </vx-dropdown-item>
                    <vx-dropdown-item v-if="showGoToContext" data-test="go-to-context" @click="goToContext()">
                        Go To Context
                        <font-awesome-icon widthAuto :icon="faSquareArrowUpRight" />
                    </vx-dropdown-item>
                    <vx-dropdown-item data-test="delete" @click="deleteCampaign()">
                        Delete
                        <font-awesome-icon widthAuto :icon="faTrash"></font-awesome-icon>
                    </vx-dropdown-item>
                </template>
            </vx-dropdown>
        </div>

        <reschedule-campaign-modal
            v-model:visible="rescheduleModalVisible"
            :campaign="campaign"
            :orgUnitId="props.orgUnitId"
            @rescheduled="handleRescheduled"
        />

        <edit-campaign-message-modal
            v-model:visible="editMessageModalVisible"
            :campaign="campaign"
            :orgUnitId="props.orgUnitId"
            @updated="handleMessageUpdated"
        />
    </vx-box>
</template>

<script setup lang="ts">
import { useCopyText } from '~/composables/useCopyText';
import {
    VxButton,
    VxBox,
    VxDropdown,
    VxDropdownItem,
    vxFormatCount,
    VxPopover,
    VxSwal,
} from '@voxie/frontend-components';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { computed, inject, ref } from 'vue';
import type { BlastForecastCampaignsResponse } from '~/services/blastForecast/blastForecastClient.types';
import { toShortDateTimeRelativeDays } from '~/utils/date';
import MessageCloud from '~/components/messages/browser-extension/components/misc/MessageCloud.vue';
import {
    faEllipsis,
    faTrash,
    faPause,
    faCalendar,
    faCrown,
    faBuildingFlag,
    faGrid2Plus,
    faFilePen,
    faSquareArrowUpRight,
    type IconDefinition,
} from '@fortawesome/pro-solid-svg-icons';
import blastForecastClient from '~/services/blastForecast/blastForecastClient';
import campaignsClient from '~/services/campaignsClient';
import { throwSwalRequestError } from '~/utils/request';
import { useToasted } from '~/composables/useToasted';
import CampaignBadge from '~/components/campaigns/campaign-card/CampaignBadge.vue';
import type { Features } from '~/provides/features.types';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { useContextSwitch } from '~/composables/useContextSwitch';
import type { CampaignStatus } from '~/constants/campaign';
import MetaBadge from '~/components/general/MetaBadge.vue';
import type { MetaBadgeColor } from '~/components/general/MetaBadge.types';
import { currencyCents } from '~/utils/helpers';
import RescheduleCampaignModal from './RescheduleCampaignModal.vue';
import EditCampaignMessageModal from './EditCampaignMessageModal.vue';

const FEATURES = inject('FEATURES') as Features;

const teamId = Spark.state.currentTeam.id;

const emit = defineEmits<{
    (e: 'deleted', campaignId: number): void;
    (e: 'statusChanged', campaignId: number, newStatus: CampaignStatus): void;
}>();

const campaign = defineModel<BlastForecastCampaignsResponse['items'][0]>('campaign', { required: true });

const props = defineProps<{
    timezone?: string;
    orgUnitId?: number;
    showEstimatedCost: boolean;
}>();

const senderBadge = computed(
    (): {
        icon: IconDefinition;
        color: MetaBadgeColor;
        label: string;
    } | void => {
        if (!FEATURES.contact_groups) {
            return;
        }

        const senderType = campaign.value?.sender.type;

        switch (senderType) {
            case 'corporate':
                return {
                    icon: faCrown,
                    color: 'amber',
                    label: 'Corporate',
                };
            case 'org_unit':
                return {
                    icon: faBuildingFlag,
                    color: 'violet',
                    label: campaign.value?.sender.name ?? '',
                };
            case 'group':
                return {
                    icon: faGrid2Plus,
                    color: 'slate',
                    label: campaign.value?.sender.name ?? '',
                };
        }

        return undefined;
    }
);

const { copyText } = useCopyText();

const toasted = useToasted();

const { onCorporate } = useCurrentGroup();
const { switchContext } = useContextSwitch();

const showGoToContext = computed((): boolean => {
    if (!FEATURES.contact_groups || (campaign.value?.status !== 'draft' && campaign.value?.status !== 'scheduled')) {
        return false;
    }

    const senderType = campaign.value?.sender.type;
    if (onCorporate()) {
        return senderType === 'org_unit' || senderType === 'group';
    }

    if (props.orgUnitId) {
        return senderType === 'group';
    }

    return false;
});

const goToContext = async (): Promise<void> => {
    const sender = campaign.value?.sender;
    if (!sender || null === sender.id) {
        return;
    }

    const type = sender.type === 'group' ? 'group' : 'orgUnit';
    try {
        await switchContext(type, sender.id);
        window.location.assign(`/messaging/quick-blast/${campaign.value?.id}`);
    } catch {
        toasted.error('Failed to switch context');
    }
};

const showGoToCampaign = computed((): boolean => {
    if (campaign.value?.status !== 'draft' && campaign.value?.status !== 'scheduled') {
        return false;
    }

    if (onCorporate() && campaign.value?.sender.type === 'corporate') {
        return true;
    }

    return (
        !!props.orgUnitId && campaign.value?.sender.type === 'org_unit' && campaign.value?.sender.id === props.orgUnitId
    );
});

const goToCampaignHref = computed((): string => {
    if (onCorporate()) {
        return `/messaging/campaigns/${campaign.value?.id}`;
    }
    return `/messaging/quick-blast/${campaign.value?.id}`;
});

const editMessageModalVisible = ref(false);

const showDropdown = computed((): boolean => {
    if (onCorporate()) {
        return true;
    }
    if (props.orgUnitId && campaign.value?.sender.type === 'corporate') {
        return false;
    }
    return !!props.orgUnitId;
});

const canEditMessage = computed(() => {
    const status = campaign.value?.status;
    if (status !== 'draft' && status !== 'scheduled') {
        return false;
    }

    if (campaign.value?.sender.type === 'corporate') {
        return false;
    }

    if (onCorporate()) {
        return true;
    }

    if (props.orgUnitId && campaign.value?.sender.type === 'org_unit') {
        return props.orgUnitId === campaign.value?.sender.id;
    }

    return !!props.orgUnitId;
});

const handleMessageUpdated = (newMessage: {
    body: string;
    media_url: string | null;
    media_content_type: string | null;
}) => {
    if (campaign.value) {
        campaign.value.main_message = {
            ...campaign.value.main_message,
            ...newMessage,
        };
    }
};

const rescheduleModalVisible = ref(false);

const canReschedule = computed(() => {
    const status = campaign.value?.status;

    if (status !== 'draft' && status !== 'scheduled') {
        return false;
    }

    return onCorporate() && campaign.value?.sender.type !== 'corporate';
});

const handleRescheduled = (liveAt: string) => {
    if (campaign.value) {
        campaign.value.live_at = liveAt;
    }
};

const setToDraft = () => {
    VxSwal.confirmation(
        'Set Campaign to Draft',
        'Are you sure you want to set this campaign to draft?',
        'question',
        async () => {
            if (!campaign.value) {
                return;
            }

            try {
                if (campaign.value.sender.type === 'corporate') {
                    await campaignsClient.changeStatus(teamId, campaign.value.id, 'draft');
                } else {
                    const context = { teamId, orgUnitId: props.orgUnitId };
                    await blastForecastClient.adjustCampaign(context, campaign.value.id, { status: 'draft' });
                }
                emit('statusChanged', campaign.value.id, 'draft');
                toasted.success('Campaign set to draft');
            } catch (e) {
                throwSwalRequestError(e);
            }
        }
    );
};

const deleteCampaign = () => {
    VxSwal.confirmation(
        'Delete Campaign',
        'Are you sure? This campaign and its messages will be deleted. There is no undo.',
        'danger',
        async () => {
            if (!campaign.value) {
                return;
            }

            try {
                if (campaign.value.sender.type === 'corporate') {
                    await campaignsClient.campaignDelete(teamId, campaign.value.id);
                } else {
                    const context = { teamId, orgUnitId: props.orgUnitId };
                    await blastForecastClient.deleteCampaign(context, campaign.value.id);
                }
                emit('deleted', campaign.value.id);
                toasted.success('Campaign deleted');
            } catch (e) {
                throwSwalRequestError(e);
            }
        }
    );
};
</script>
